import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import {
  adminApi,
  adminAuth,
  type Lead,
  type LeadSource,
  type LeadStatus,
} from '../lib/adminApi';

const STATUS_ORDER: LeadStatus[] = ['NEW', 'CONTACTED', 'REPLIED', 'WON', 'LOST'];
const STATUS_LABEL: Record<LeadStatus, string> = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  REPLIED: 'Replied',
  WON: 'Won',
  LOST: 'Lost',
};
const STATUS_TONE: Record<LeadStatus, string> = {
  NEW: 'bg-blue-50 text-blue-700 ring-blue-200',
  CONTACTED: 'bg-amber-50 text-amber-700 ring-amber-200',
  REPLIED: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  WON: 'bg-green-50 text-green-700 ring-green-200',
  LOST: 'bg-slate-100 text-slate-600 ring-slate-200',
};

const SOURCE_LABEL: Record<LeadSource, string> = {
  COLD_EMAIL: 'Cold email',
  CONTACT_FORM: 'Contact form',
  WIZARD_ABANDON: 'Wizard abandon',
  REFERRAL: 'Referral',
  OTHER: 'Other',
};

export function AdminLeads() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Default to 'active' which means: show everything except LOST. The
  // operator can flip to 'all' or any specific status with the filter
  // chips. Lost leads stay in the database — they just don't clutter the
  // main view.
  const [filterStatus, setFilterStatus] = useState<LeadStatus | '' | 'active'>('active');
  const [q, setQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);

  function refresh() {
    setLoading(true);
    // 'active' is a frontend-only filter that translates to "anything but
    // LOST". The API itself doesn't know about it — we fetch all and
    // filter client-side. Volume is small (<1k leads) so this is cheap.
    const apiStatus =
      filterStatus === 'active' || filterStatus === '' ? undefined : filterStatus;
    adminApi
      .listLeads({
        status: apiStatus,
        q: q.trim() || undefined,
      })
      .then((r) => {
        if (filterStatus === 'active') {
          setItems(r.items.filter((l) => l.status !== 'LOST'));
        } else {
          setItems(r.items);
        }
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load leads'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!adminAuth.isLoggedIn()) return navigate('/admin/login');
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, q]);

  const counts: Record<LeadStatus | 'ALL', number> = {
    ALL: items.length,
    NEW: 0, CONTACTED: 0, REPLIED: 0, WON: 0, LOST: 0,
  };
  for (const l of items) counts[l.status]++;

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Leads</h1>
          <p className="mt-1 text-sm text-slate-500">
            Pre-payment pipeline. Push prospects from New → Contacted → Won.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowImport(true)}
            className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            ⬆ Import CSV
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="rounded-md bg-brand-700 px-3 py-2 text-sm font-medium text-white hover:bg-brand-800"
          >
            + Add lead
          </button>
        </div>
      </div>

      {error && (
        <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {/* Filter chips */}
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <FilterChip
          label={`Active (${counts.NEW + counts.CONTACTED + counts.REPLIED + counts.WON})`}
          active={filterStatus === 'active'}
          onClick={() => setFilterStatus('active')}
        />
        <FilterChip
          label={`All (${counts.ALL})`}
          active={filterStatus === ''}
          onClick={() => setFilterStatus('')}
        />
        {STATUS_ORDER.map((s) => (
          <FilterChip
            key={s}
            label={`${STATUS_LABEL[s]} (${counts[s]})`}
            active={filterStatus === s}
            onClick={() => setFilterStatus(s)}
            tone={STATUS_TONE[s]}
          />
        ))}
        <span className="flex-1" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name, email, company…"
          className="input max-w-[280px] !py-1.5 !text-sm"
        />
      </div>

      <div className="mt-6 overflow-hidden rounded-xl bg-white ring-1 ring-slate-200">
        {loading ? (
          <div className="px-5 py-8 text-sm text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
              👤
            </div>
            <h2 className="mt-4 text-base font-semibold text-slate-900">
              No leads yet
            </h2>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
              Add cold-email replies, contact-form messages worth chasing,
              or wizard abandons as leads to track their progress.
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">ID / Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Businesses</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Last touch</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((l) => (
                <LeadRow key={l.id} lead={l} onChange={refresh} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && (
        <AddLeadModal
          onClose={() => setShowAdd(false)}
          onCreated={() => {
            setShowAdd(false);
            refresh();
          }}
        />
      )}

      {showImport && (
        <ImportLeadsModal
          onClose={() => setShowImport(false)}
          onDone={() => {
            setShowImport(false);
            refresh();
          }}
        />
      )}
    </AdminLayout>
  );
}

function FilterChip({
  label,
  active,
  onClick,
  tone,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  tone?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors ${
        active
          ? tone || 'bg-brand-50 text-brand-700 ring-brand-200'
          : 'bg-white text-slate-600 ring-slate-200 hover:bg-slate-50'
      }`}
    >
      {label}
    </button>
  );
}

function LeadRow({ lead, onChange }: { lead: Lead; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  async function setStatus(status: LeadStatus) {
    setBusy(true);
    try {
      await adminApi.updateLead(lead.id, { status });
      onChange();
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(`Delete lead "${lead.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await adminApi.deleteLead(lead.id);
      onChange();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <tr className="hover:bg-slate-50">
        <td className="px-4 py-3">
          <div className="flex flex-col gap-1">
            <IdChip displayId={lead.displayId} isPaid={!!lead.isPaid} />
            <div className="font-medium text-slate-900">{lead.name}</div>
            {lead.phone && (
              <div className="text-xs text-slate-500">{lead.phone}</div>
            )}
          </div>
        </td>
        <td className="px-4 py-3">
          <a href={`mailto:${lead.email}`} className="text-brand-700 hover:underline">
            {lead.email}
          </a>
        </td>
        <td className="px-4 py-3 text-slate-700">
          <BusinessesCell lead={lead} onChange={onChange} isPaid={!!lead.isPaid} />
        </td>
        <td className="px-4 py-3 text-xs text-slate-500">{SOURCE_LABEL[lead.source]}</td>
        <td className="px-4 py-3">
          <select
            value={lead.status}
            onChange={(e) => setStatus(e.target.value as LeadStatus)}
            disabled={busy}
            className={`rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset focus:outline-none ${STATUS_TONE[lead.status]}`}
          >
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
        </td>
        <td className="px-4 py-3 text-right text-xs text-slate-500">
          {lead.lastContactedAt
            ? new Date(lead.lastContactedAt).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'short',
              })
            : '–'}
        </td>
        <td className="px-4 py-3 text-right">
          <div className="flex items-center justify-end gap-3 text-xs">
            <button
              onClick={() => setShowNotes((v) => !v)}
              className="text-brand-700 hover:underline"
            >
              {showNotes ? 'Hide' : 'Notes'}
            </button>
            <button
              onClick={remove}
              disabled={busy}
              className="text-slate-400 hover:text-red-600"
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
      {showNotes && (
        <tr className="bg-slate-50">
          <td colSpan={7} className="px-4 py-3">
            <NotesEditor lead={lead} onSaved={onChange} />
          </td>
        </tr>
      )}
    </>
  );
}

function NotesEditor({ lead, onSaved }: { lead: Lead; onSaved: () => void }) {
  const [text, setText] = useState(lead.notes || '');
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      await adminApi.updateLead(lead.id, { notes: text });
      onSaved();
    } finally {
      setBusy(false);
    }
  }
  return (
    <div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder="Internal notes — most recent at top"
        className="input w-full !text-sm"
      />
      <div className="mt-2 flex justify-end">
        <button
          onClick={save}
          disabled={busy}
          className="rounded-md bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800"
        >
          {busy ? 'Saving…' : 'Save notes'}
        </button>
      </div>
    </div>
  );
}

function AddLeadModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [source, setSource] = useState<LeadSource>('OTHER');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!name.trim() || !email.trim()) {
      setErr('Name and email are required');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await adminApi.createLead({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        companyName: companyName.trim() || undefined,
        companyNumber: companyNumber.trim() || undefined,
        source,
        notes: notes.trim() || undefined,
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not create lead');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Add a lead</h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            ✕
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <Field label="Name *">
            <input value={name} onChange={(e) => setName(e.target.value)} className="input" autoFocus />
          </Field>
          <Field label="Email *">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Phone">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className="input" />
          </Field>
          <Field label="Source">
            <select
              value={source}
              onChange={(e) => setSource(e.target.value as LeadSource)}
              className="input"
            >
              {(Object.keys(SOURCE_LABEL) as LeadSource[]).map((s) => (
                <option key={s} value={s}>{SOURCE_LABEL[s]}</option>
              ))}
            </select>
          </Field>
          <Field label="Company name">
            <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="input" />
          </Field>
          <Field label="Company number">
            <input
              value={companyNumber}
              onChange={(e) => setCompanyNumber(e.target.value.toUpperCase())}
              maxLength={10}
              className="input font-mono"
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Notes">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="input"
              />
            </Field>
          </div>
        </div>

        {err && <div className="mt-3 rounded-md bg-red-50 p-2 text-xs text-red-700">{err}</div>}

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            className="rounded-md bg-brand-700 px-3 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:bg-slate-300"
          >
            {busy ? 'Adding…' : 'Add lead'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Bulk import modal — accepts a CSV file or pasted text, parses
 * Name/Email/Phone/Company Name/Company Number/Notes columns, previews
 * the rows, then POSTs them to /admin/leads/bulk-import.
 *
 * Expected header (case-insensitive, order-flexible):
 *   Name, Email, Phone, Company Name, Company Number, Notes
 */
type CsvRow = {
  name: string;
  email: string;
  phone?: string;
  companyName?: string;
  companyNumber?: string;
  notes?: string;
};

function ImportLeadsModal({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: () => void;
}) {
  const [raw, setRaw] = useState('');
  const [source, setSource] = useState<LeadSource>('COLD_EMAIL');
  const [parsed, setParsed] = useState<CsvRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<
    { imported: number; skippedDup: number; errors: { row: number; reason: string }[] } | null
  >(null);

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      setRaw(text);
      parse(text);
    };
    reader.readAsText(f);
  }

  function parse(text: string) {
    setParseError(null);
    setParsed([]);
    if (!text.trim()) return;
    try {
      const rows = parseCsv(text);
      if (rows.length < 2) {
        setParseError(
          'Need at least a header row plus one data row. ' +
            'Header should contain Name and Email.',
        );
        return;
      }
      const headers = rows[0].map((h) => h.trim().toLowerCase());
      const idx = {
        name: headers.indexOf('name'),
        email: headers.indexOf('email'),
        phone: headers.indexOf('phone'),
        companyName: headers.findIndex((h) => h === 'company name' || h === 'company'),
        companyNumber: headers.findIndex(
          (h) => h === 'company number' || h === 'company no' || h === 'co. number',
        ),
        notes: headers.indexOf('notes'),
      };
      if (idx.name === -1 || idx.email === -1) {
        setParseError('CSV must include "Name" and "Email" columns in the header row.');
        return;
      }
      const out: CsvRow[] = [];
      for (let i = 1; i < rows.length; i++) {
        const cols = rows[i];
        // Skip blank lines.
        if (cols.every((c) => !c.trim())) continue;
        const name = (cols[idx.name] || '').trim();
        const email = (cols[idx.email] || '').trim();
        if (!name || !email) continue;
        const row: CsvRow = { name, email };
        if (idx.phone >= 0) row.phone = (cols[idx.phone] || '').trim() || undefined;
        if (idx.companyName >= 0)
          row.companyName = (cols[idx.companyName] || '').trim() || undefined;
        if (idx.companyNumber >= 0)
          row.companyNumber = (cols[idx.companyNumber] || '').trim().toUpperCase() || undefined;
        if (idx.notes >= 0) row.notes = (cols[idx.notes] || '').trim() || undefined;
        out.push(row);
      }
      setParsed(out);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Could not parse CSV');
    }
  }

  async function submit() {
    if (parsed.length === 0) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await adminApi.bulkImportLeads({ rows: parsed, source });
      setResult(r);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Import leads from CSV</h2>
            <p className="mt-1 text-xs text-slate-500">
              Expected columns: Name, Email, Phone, Company Name, Company Number, Notes.
              Name and Email are required.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            ✕
          </button>
        </div>

        {!result ? (
          <>
            <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_220px]">
              <Field label="CSV file">
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={onFile}
                  className="block w-full text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-200"
                />
              </Field>
              <Field label="Source for all imported">
                <select
                  value={source}
                  onChange={(e) => setSource(e.target.value as LeadSource)}
                  className="input"
                >
                  <option value="COLD_EMAIL">Cold email</option>
                  <option value="CONTACT_FORM">Contact form</option>
                  <option value="WIZARD_ABANDON">Wizard abandon</option>
                  <option value="REFERRAL">Referral</option>
                  <option value="OTHER">Other</option>
                </select>
              </Field>
            </div>

            <div className="mt-3">
              <Field label="Or paste CSV text directly">
                <textarea
                  value={raw}
                  onChange={(e) => {
                    setRaw(e.target.value);
                    parse(e.target.value);
                  }}
                  rows={6}
                  placeholder="Name,Email,Phone,Company Name,Company Number,Notes&#10;John Smith,john@acme.co.uk,07700900123,Acme Ltd,12345678,"
                  className="input w-full font-mono !text-xs"
                />
              </Field>
            </div>

            {parseError && (
              <div className="mt-3 rounded-md bg-red-50 p-2 text-xs text-red-700">{parseError}</div>
            )}

            {parsed.length > 0 && (
              <div className="mt-4">
                <div className="mb-2 text-xs font-semibold text-slate-700">
                  Preview · {parsed.length} row{parsed.length === 1 ? '' : 's'} ready
                </div>
                <div className="max-h-48 overflow-auto rounded-md border border-slate-200">
                  <table className="w-full text-[11px]">
                    <thead className="sticky top-0 bg-slate-50 text-left text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="px-3 py-1.5">Name</th>
                        <th className="px-3 py-1.5">Email</th>
                        <th className="px-3 py-1.5">Phone</th>
                        <th className="px-3 py-1.5">Company</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.slice(0, 100).map((r, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-3 py-1">{r.name}</td>
                          <td className="px-3 py-1">{r.email}</td>
                          <td className="px-3 py-1">{r.phone || '–'}</td>
                          <td className="px-3 py-1">{r.companyName || '–'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsed.length > 100 && (
                    <div className="border-t border-slate-100 bg-slate-50 px-3 py-1 text-[10px] text-slate-500">
                      …and {parsed.length - 100} more rows
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={onClose} className="btn-ghost">Cancel</button>
              <button
                onClick={submit}
                disabled={busy || parsed.length === 0}
                className="rounded-md bg-brand-700 px-3 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:bg-slate-300"
              >
                {busy ? 'Importing…' : `Import ${parsed.length || ''} lead${parsed.length === 1 ? '' : 's'}`}
              </button>
            </div>
          </>
        ) : (
          <div className="mt-5">
            <div className="rounded-lg bg-green-50 p-4 text-sm text-green-800">
              <div className="text-base font-semibold">Import complete</div>
              <ul className="mt-2 list-disc pl-5">
                <li>{result.imported} added</li>
                <li>{result.skippedDup} skipped (already in leads)</li>
                {result.errors.length > 0 && (
                  <li>{result.errors.length} failed (see below)</li>
                )}
              </ul>
            </div>
            {result.errors.length > 0 && (
              <div className="mt-3 max-h-40 overflow-auto rounded-md bg-red-50 p-3 text-[11px] text-red-700">
                <ul className="space-y-1">
                  {result.errors.map((e) => (
                    <li key={e.row}>
                      Row {e.row}: {e.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-5 flex justify-end">
              <button
                onClick={onDone}
                className="rounded-md bg-brand-700 px-3 py-2 text-sm font-medium text-white hover:bg-brand-800"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Tiny RFC-4180-ish CSV parser. Handles double-quoted fields, escaped
 * quotes ("") inside fields, and \r\n / \n line endings. Doesn't handle
 * obscure edge cases like newlines inside fields without quoting — that's
 * fine for spreadsheet exports which always quote-multiline cells.
 */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let cur: string[] = [];
  let field = '';
  let inQuote = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuote) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuote = false;
        }
      } else {
        field += ch;
      }
    } else {
      if (ch === '"') {
        inQuote = true;
      } else if (ch === ',') {
        cur.push(field);
        field = '';
      } else if (ch === '\n' || ch === '\r') {
        if (ch === '\r' && text[i + 1] === '\n') i++;
        cur.push(field);
        rows.push(cur);
        cur = [];
        field = '';
      } else {
        field += ch;
      }
    }
  }
  if (field !== '' || cur.length > 0) {
    cur.push(field);
    rows.push(cur);
  }
  return rows;
}

/**
 * The colour-coded display-ID pill shown next to lead names and business
 * company names. Cream/yellow while the associated lead hasn't yet
 * turned into a paid customer, green after payment.
 */
function IdChip({
  displayId,
  isPaid,
  size = 'md',
}: {
  displayId: string | null;
  isPaid: boolean;
  size?: 'sm' | 'md';
}) {
  if (!displayId) return null;
  const tone = isPaid
    ? 'bg-green-100 text-green-800 ring-green-300'
    : 'bg-amber-100 text-amber-800 ring-amber-300';
  const sizing = size === 'sm' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-0.5';
  return (
    <span
      className={`inline-flex w-fit items-center rounded-full font-mono font-semibold ring-1 ring-inset ${tone} ${sizing}`}
      title={isPaid ? 'Converted — paid customer' : 'Unpaid lead'}
    >
      {displayId}
    </span>
  );
}

/**
 * The businesses cell inside the leads table. Renders each business as
 * an ID chip + company name/number, with an add-business + inline
 * remove flow.
 */
function BusinessesCell({
  lead,
  onChange,
  isPaid,
}: {
  lead: Lead;
  onChange: () => void;
  isPaid: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [busy, setBusy] = useState(false);

  async function addBusiness() {
    if (!companyName.trim()) return;
    setBusy(true);
    try {
      await adminApi.addBusiness(lead.id, {
        companyName: companyName.trim(),
        companyNumber: companyNumber.trim() || undefined,
      });
      setCompanyName('');
      setCompanyNumber('');
      setAdding(false);
      onChange();
    } finally {
      setBusy(false);
    }
  }

  async function removeBusiness(bizId: string, name: string) {
    if (!confirm(`Remove ${name} from this lead? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await adminApi.deleteBusiness(bizId);
      onChange();
    } finally {
      setBusy(false);
    }
  }

  const list = lead.businesses || [];
  return (
    <div className="min-w-0">
      {list.length === 0 && !adding && (
        <div className="text-xs text-slate-400">No businesses linked</div>
      )}
      <ul className="space-y-1.5">
        {list.map((b) => (
          <li key={b.id} className="flex items-start gap-2">
            <IdChip displayId={b.displayId} isPaid={isPaid} size="sm" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-slate-900">{b.companyName}</div>
              {b.companyNumber && (
                <div className="font-mono text-[10px] text-slate-500">{b.companyNumber}</div>
              )}
            </div>
            <button
              onClick={() => removeBusiness(b.id, b.companyName)}
              disabled={busy}
              className="text-[10px] text-slate-400 hover:text-red-600"
              title="Remove business"
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
      {adding ? (
        <div className="mt-2 space-y-1.5">
          <input
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Company name"
            className="input !py-1 !text-xs"
            autoFocus
          />
          <input
            value={companyNumber}
            onChange={(e) => setCompanyNumber(e.target.value.toUpperCase())}
            placeholder="Company number (optional)"
            maxLength={10}
            className="input !py-1 !text-xs font-mono"
          />
          <div className="flex gap-1.5">
            <button
              onClick={addBusiness}
              disabled={busy || !companyName.trim()}
              className="rounded-md bg-brand-700 px-2 py-1 text-[11px] font-medium text-white hover:bg-brand-800 disabled:bg-slate-300"
            >
              {busy ? 'Adding…' : 'Add'}
            </button>
            <button
              onClick={() => { setAdding(false); setCompanyName(''); setCompanyNumber(''); }}
              className="text-[11px] text-slate-500 hover:text-slate-900"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="mt-1.5 text-[11px] text-brand-700 hover:underline"
        >
          + Add business
        </button>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
